import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
} from '@chakra-ui/react';
import { FC, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { PurchaseRewardPromotion } from '@olagg/db-types';
import OlaGGFormInput from '../../components/Forms/OlaggFormInput';
import OlaggForm from '../../components/Forms/OlaggForm';
import OlaggFormSelect from '../../components/Forms/OlaggFormSelect';
import { fromLocalDateTimeToUTC, toLocalTimezone } from '../../utils';
import useProduct from '../Marketplace/products/hooks/useProduct';
import { fetcher, AdminProductsEndpoints } from '@olagg/api-hooks';

type IProps = {
  initialData?: PurchaseRewardPromotion | undefined;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
};

export type PurchaseRewardPromotionFormData = {
  name: string;
  description?: string;
  scope: string;
  category?: string;
  fixed_xp: number;
  fixed_treasure_opportunities: number;
  fixed_claw_coins: number;
  xp_per_usdc: number;
  treasure_per_usdc: number;
  claw_coins_per_usdc: number;
  coin_expiration_days?: number;
  treasure_opportunity_expiration_days?: number;
  starts_at?: Date;
  ends_at?: Date;
  active: boolean;
  max_uses_per_user?: number;
  max_total_uses?: number;
  product_ids?: number[];
};

const scopeOptions = [
  { value: 'all_products', label: 'Todos los productos' },
  { value: 'category', label: 'Categoría específica' },
  { value: 'specific_products', label: 'Productos específicos' }
];

const PurchaseRewardPromotionForm: FC<IProps> = ({ initialData, onSubmit, onCancel, loading = false, isEdit = false }) => {
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { categories, allCategories } = useProduct();

  const [categoryOptions, setCategoryOptions] = useState<{value: string, label: string}[]>([]);
  const [productOptions, setProductOptions] = useState<{value: string, label: string}[]>(() => {
    // Initialize with products from initialData if editing
    if (initialData?.products && initialData.products.length > 0) {
      return initialData.products.map((p: any) => ({
        value: String(p.id),
        label: `${p.name} (${p.category})`
      }));
    }
    return [];
  });
  const [selectedScope, setSelectedScope] = useState<string>(initialData?.scope || 'all_products');

  useEffect(() => {
    allCategories();
    loadProducts('');
  }, []);

  useEffect(() => {
    if (categories.length > 0) {
      setCategoryOptions(categories.map(cat => ({ value: cat, label: cat })));
    }
  }, [categories]);

  const loadProducts = async (inputValue: string) => {
    try {
      const response = await fetcher(AdminProductsEndpoints.all({
        page: 1,
        limit: 100,
        name: inputValue || undefined
      }));
      const options = response.products.map((p: any) => ({
        value: String(p.id),
        label: `${p.name} (${p.category})`
      }));

      // Merge with existing options and deduplicate
      setProductOptions(prev => {
        const merged = [...prev, ...options];
        const unique = merged.filter((opt, index, self) =>
          index === self.findIndex((t) => t.value === opt.value)
        );
        return unique;
      });

      return options;
    } catch (error) {
      console.error('Error loading products:', error);
      return [];
    }
  };

  const useFormMethods = useForm<PurchaseRewardPromotionFormData>({
    mode: 'onChange',
    defaultValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      scope: initialData?.scope || 'all_products',
      category: initialData?.category || '',
      fixed_xp: initialData?.fixedXp || 0,
      fixed_treasure_opportunities: initialData?.fixedTreasureOpportunities || 0,
      fixed_claw_coins: initialData?.fixedClawCoins || 0,
      xp_per_usdc: initialData?.xpPerUsdc || 0,
      treasure_per_usdc: initialData?.treasurePerUsdc || 0,
      claw_coins_per_usdc: initialData?.clawCoinsPerUsdc || 0,
      coin_expiration_days: initialData?.coinExpirationDays || undefined,
      treasure_opportunity_expiration_days: initialData?.treasureOpportunityExpirationDays || undefined,
      starts_at: initialData?.startsAt ? toLocalTimezone(initialData.startsAt) : undefined,
      ends_at: initialData?.endsAt ? toLocalTimezone(initialData.endsAt) : undefined,
      active: initialData?.active ?? true,
      max_uses_per_user: initialData?.maxUsesPerUser || undefined,
      max_total_uses: initialData?.maxTotalUses || undefined,
      product_ids: initialData?.products?.map(p => ({ value: String(p.id), label: `${p.name} (${p.category})` })) || []
    }
  });

  const {
    watch,
    setValue,
    formState: { isSubmitting }
  } = useFormMethods;

  const watchedScope = watch('scope');

  useEffect(() => {
    // Handle scope being an object or string
    const scopeValue = typeof watchedScope === 'object' && watchedScope !== null
      ? (watchedScope as any).value
      : watchedScope;
    setSelectedScope(scopeValue || 'all_products');
  }, [watchedScope]);

  const beforeSubmit = async (data: PurchaseRewardPromotionFormData) => {
    const scopeValue = typeof data.scope === 'object' && data.scope !== null
      ? (data.scope as any).value
      : data.scope;

    const categoryValue = typeof data.category === 'object' && data.category !== null
      ? (data.category as any).value
      : data.category;

    let productUids: string[] = [];
    if (scopeValue === 'specific_products' && data.product_ids) {
      productUids = (data.product_ids as any[]).map(p =>
        typeof p === 'object' ? p.value : p
      );
    }

    const finalData = {
      name: data.name,
      description: data.description,
      scope: scopeValue,
      category: scopeValue === 'category' ? categoryValue : null,
      fixed_xp: data.fixed_xp,
      fixed_treasure_opportunities: data.fixed_treasure_opportunities,
      fixed_claw_coins: data.fixed_claw_coins,
      xp_per_usdc: data.xp_per_usdc,
      treasure_per_usdc: data.treasure_per_usdc,
      claw_coins_per_usdc: data.claw_coins_per_usdc,
      coin_expiration_days: data.coin_expiration_days || null,
      treasure_opportunity_expiration_days: data.treasure_opportunity_expiration_days || null,
      starts_at: data.starts_at ? fromLocalDateTimeToUTC(data.starts_at) : null,
      ends_at: data.ends_at ? fromLocalDateTimeToUTC(data.ends_at) : null,
      active: data.active,
      max_uses_per_user: data.max_uses_per_user || null,
      max_total_uses: data.max_total_uses || null,
      product_uids: productUids
    };
    await onSubmit(finalData);
  };

  return (
    <OlaggForm
      onSubmit={beforeSubmit}
      useFormMethods={useFormMethods}
      style={{
        width: '100%'
      }}
    >
      <VStack color={'white'} w={'full'} maxW='800px' margin='0 auto' spacing={4}>

        {/* Información básica */}
        <Box w="full" p={4} bg="gray.800" borderRadius="md">
          <Text fontWeight="bold" mb={4}>Información básica</Text>

          <OlaGGFormInput
            label='Nombre'
            controlName='name'
            placeholder='Nombre de la promoción'
            required={true}
          />

          <OlaGGFormInput
            label='Descripción'
            controlName='description'
            placeholder='Descripción opcional'
            inputType='textarea'
          />

          <OlaGGFormInput
            label='Activa'
            controlName='active'
            inputType='switch'
            required={false}
          />
        </Box>

        {/* Alcance */}
        <Box w="full" p={4} bg="gray.800" borderRadius="md">
          <Text fontWeight="bold" mb={4}>Alcance de la promoción</Text>

          <OlaggFormSelect
            label='Tipo de alcance'
            controlName='scope'
            placeholder='Selecciona el alcance'
            staticOptions={scopeOptions}
            required={true}
            onChange={(value) => {
              const scopeValue = typeof value === 'object' && value !== null ? value.value : value;
              setSelectedScope(scopeValue || 'all_products');
            }}
          />

          {selectedScope === 'category' && (
            <OlaggFormSelect
              label='Categoría'
              controlName='category'
              placeholder='Selecciona una categoría'
              staticOptions={categoryOptions}
              isSearchable
              required={true}
            />
          )}

          {selectedScope === 'specific_products' && (
            <OlaggFormSelect
              label='Productos'
              controlName='product_ids'
              placeholder='Selecciona productos'
              staticOptions={productOptions}
              asyncOptions={loadProducts}
              isSearchable
              multiple
              required={true}
              newVersion
            />
          )}
        </Box>

        {/* Recompensas fijas */}
        <Box w="full" p={4} bg="gray.800" borderRadius="md">
          <Text fontWeight="bold" mb={4}>Recompensas fijas (por compra)</Text>
          <Text fontSize="sm" color="gray.400" mb={4}>
            Se otorgan una vez por cada compra elegible
          </Text>

          <HStack spacing={4} w="full">
            <Box flex={1}>
              <OlaGGFormInput
                label='XP fijo'
                controlName='fixed_xp'
                placeholder='0'
                inputType='number'
              />
            </Box>
            <Box flex={1}>
              <OlaGGFormInput
                label='Oportunidades de tesoro'
                controlName='fixed_treasure_opportunities'
                placeholder='0'
                inputType='number'
              />
            </Box>
            <Box flex={1}>
              <OlaGGFormInput
                label='Monedas de garra'
                controlName='fixed_claw_coins'
                placeholder='0'
                inputType='number'
              />
            </Box>
          </HStack>
        </Box>

        {/* Recompensas proporcionales */}
        <Box w="full" p={4} bg="gray.800" borderRadius="md">
          <Text fontWeight="bold" mb={4}>Recompensas proporcionales (por USDC gastado)</Text>
          <Text fontSize="sm" color="gray.400" mb={4}>
            Se multiplican por la cantidad de USDC de la compra
          </Text>

          <HStack spacing={4} w="full">
            <Box flex={1}>
              <OlaGGFormInput
                label='XP por USDC'
                controlName='xp_per_usdc'
                placeholder='0'
                inputType='number'
              />
            </Box>
            <Box flex={1}>
              <OlaGGFormInput
                label='Tesoros por USDC'
                controlName='treasure_per_usdc'
                placeholder='0'
                inputType='number'
                chakraStyles={{ type: 'number', step: 1 }}
              />
            </Box>
            <Box flex={1}>
              <OlaGGFormInput
                label='Monedas por USDC'
                controlName='claw_coins_per_usdc'
                placeholder='0'
                inputType='number'
                chakraStyles={{ type: 'number', step: 1 }}
              />
            </Box>
          </HStack>
        </Box>

        {/* Expiración de recompensas */}
        <Box w="full" p={4} bg="gray.800" borderRadius="md">
          <Text fontWeight="bold" mb={4}>Expiración de recompensas</Text>
          <Text fontSize="sm" color="gray.400" mb={4}>
            Días desde que se otorgan hasta que expiran. Dejar vacío para usar valores por defecto.
          </Text>

          <HStack spacing={4} w="full">
            <Box flex={1}>
              <OlaGGFormInput
                label='Expiración monedas (días)'
                controlName='coin_expiration_days'
                placeholder='Por defecto'
                inputType='number'
              />
            </Box>
            <Box flex={1}>
              <OlaGGFormInput
                label='Expiración tesoros (días)'
                controlName='treasure_opportunity_expiration_days'
                placeholder='7 días por defecto'
                inputType='number'
              />
            </Box>
          </HStack>
        </Box>

        {/* Vigencia */}
        <Box w="full" p={4} bg="gray.800" borderRadius="md">
          <Text fontWeight="bold" mb={4}>Vigencia</Text>
          <Text fontSize="sm" color="gray.400" mb={4}>
            Dejar vacío para que no tenga límite de tiempo
          </Text>

          <HStack spacing={4} w="full">
            <Box flex={1}>
              <OlaGGFormInput
                label='Fecha de Inicio'
                controlName='starts_at'
                inputType='datetime'
                description={`Zona horaria: ${currentTimeZone}`}
                datePickerProps={{ backgroundColor: 'white' }}
                required={false}
              />
            </Box>
            <Box flex={1}>
              <OlaGGFormInput
                label='Fecha de Fin'
                controlName='ends_at'
                inputType='datetime'
                description={`Zona horaria: ${currentTimeZone}`}
                datePickerProps={{ backgroundColor: 'white' }}
                required={false}
              />
            </Box>
          </HStack>
        </Box>

        {/* Límites */}
        <Box w="full" p={4} bg="gray.800" borderRadius="md">
          <Text fontWeight="bold" mb={4}>Límites de uso</Text>
          <Text fontSize="sm" color="gray.400" mb={4}>
            Dejar vacío para uso ilimitado
          </Text>

          <HStack spacing={4} w="full">
            <Box flex={1}>
              <OlaGGFormInput
                label='Máx. usos por usuario'
                controlName='max_uses_per_user'
                placeholder='Sin límite'
                inputType='number'
              />
            </Box>
            <Box flex={1}>
              <OlaGGFormInput
                label='Máx. usos totales'
                controlName='max_total_uses'
                placeholder='Sin límite'
                inputType='number'
              />
            </Box>
          </HStack>
        </Box>

        {/* Botones */}
        <HStack spacing={4} w="full" pt={4}>
          <Button
            variant="outline"
            onClick={onCancel}
            w="full"
          >
            Cancelar
          </Button>
          <Button
            variant={'filledGradient'}
            type="submit"
            w={'full'}
            isLoading={loading || isSubmitting}
            loadingText={isEdit ? 'Actualizando...' : 'Creando...'}
          >
            {isEdit ? 'Actualizar Promoción' : 'Crear Promoción'}
          </Button>
        </HStack>
      </VStack>
    </OlaggForm>
  );
};

export default PurchaseRewardPromotionForm;
export { PurchaseRewardPromotionForm as Form };
